import useModal from "./modalHook";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { getAllSubscriptionPlans, getSubscriptionPlanById, subscribeToPlan } from "../services/billing";
import SubscriptionPlan from "../models/SubscriptionPlan";
import ActionConfirmation from "../components/elements/ActionConfirmation";
import useUserAccount from "./userAccountHook";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { showToast } from "../utils/miscFunctions";

const useBilling = () => {
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const BillingModal = ModalComponent;
  const queryClient = useQueryClient();
  const { signOut } = useUserAccount()
  const [loading, setLoading] = useState<boolean | undefined>(undefined);
  const invalidateQueries = () => { queryClient.invalidateQueries({ queryKey: ['subscription-plans'] }) }
  const [isSubscriptionSuccessful, setIsSubscriptionSuccessful] = useState<boolean>(false);
  const fetchSubscriptionPlans = async (pageNumber?: number, pageSize?: number, search?: string) => {
    setLoading(true);
    try {
      const response: any = await getAllSubscriptionPlans(pageNumber = 0, pageSize = 10);
      if (response) {
        const plans = response?.map((plan: any) => new SubscriptionPlan(plan));
        return plans;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const fetchSubscriptionPlanById = async (id?: string) => {
    if (!id) {
      return;
    }

    setLoading(true);
    try {
      const response: any = await getSubscriptionPlanById(id);
      if (response) {
        const plan = new SubscriptionPlan(response);
        return plan;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const companySubscribeToPlan = async (planId: string) => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <ActionConfirmation
          type="success"
          onClose={closeModal}
          action="Subscribe"
          onConfirm={async () => {
            try {
              const response: any = await subscribeToPlan(planId);
              if (response) {
                showToast.success(response.message ?? 'Product status changed successfully');
                setIsSubscriptionSuccessful(true)
                setTimeout(() => {
                  signOut()
                }, 5000);

              }
            } catch (error: any) {
              showToast.error(error.description ?? 'Failed to subscribe to plan');
              invalidateQueries()
            }
          }}
        />
      ),
    });
  };

  const afterSuccessfullySubscribedModal = () => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <div className="py-6 px-4 md:px-6 w-full flex flex-col items-center text-center space-y-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-50 mb-1">
            <CheckCircleIcon className="h-7 w-7 text-green-600" />
          </div>
          <p className="text-lg font-semibold text-gray-900">Subscription successful</p>
          <p className="text-sm text-gray-600 max-w-sm">
            Your billing plan has been updated. For security reasons, you will be signed out shortly so your new subscription can fully take effect.
          </p>
          <p className="text-xs text-gray-400">You will be signed out automatically in a few seconds.</p>

          <button
            type="button"
            onClick={() => {
              signOut();
            }}
            className="mt-1 inline-flex items-center justify-center rounded-md bg-primary-main px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-main/90 focus:outline-none focus:ring-2 focus:ring-primary-main focus:ring-offset-2"
          >
            Log out now
          </button>
        </div>
      ),
    });
  }

  return {
    BillingModal,
    invalidateQueries,
    fetchSubscriptionPlans,
    fetchSubscriptionPlanById,
    companySubscribeToPlan,
    afterSuccessfullySubscribedModal,
    isSubscriptionSuccessful
  }
}

export default useBilling
